import React, {useMemo, useState} from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import {useHistory} from "react-router";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import sharingPloggingService from "./service/sharingPlogging.service";

const getErrorMessage = (e) => {
    return e?.response?.data?.message || e?.response?.data?.resultMsg || e?.message || "저장 중 오류가 발생했습니다.";
};

function SharedPloggingForm() {
    const history = useHistory();
    const [form, setForm] = useState({
        centerLoginId: "",
        price: "",
        priority: "0",
        active: true,
    });
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const thumbnailPreview = useMemo(
        () => (thumbnailFile ? URL.createObjectURL(thumbnailFile) : ""),
        [thumbnailFile],
    );
    const imagePreview = useMemo(
        () => (imageFile ? URL.createObjectURL(imageFile) : ""),
        [imageFile],
    );

    const handleChange = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSubmit = async () => {
        if (!form.centerLoginId || !form.price || !thumbnailFile || !imageFile) {
            alert("봉사센터 로그인 아이디, 금액, 썸네일, 이미지를 모두 입력해주세요.");
            return;
        }

        try {
            setSaving(true);
            const response = await sharingPloggingService.register({
                centerLoginId: form.centerLoginId.trim(),
                price: Number(form.price),
                priority: Number(form.priority || 0),
            });
            const sharingPloggingId = response?.data?.sharingPloggingId;

            if (!sharingPloggingId) {
                throw new Error("등록 응답이 올바르지 않습니다.");
            }

            const thumbnailForm = new FormData();
            thumbnailForm.append("image", thumbnailFile);
            await sharingPloggingService.fileRegister(sharingPloggingId, thumbnailForm);

            const imageForm = new FormData();
            imageForm.append("image", imageFile);
            await sharingPloggingService.imageRegister(sharingPloggingId, imageForm);

            if (!form.active) {
                await sharingPloggingService.update(sharingPloggingId, {active: false});
            }

            alert("정상적으로 등록되었습니다.");
            history.push("/admin/sharingPlogging");
        } catch (e) {
            alert(getErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <SimpleHeader name="Tables" parentName="Tables"/>
            <Container className="mt--6" fluid>
                <Row>
                    <div className="col">
                        <Card>
                            <CardHeader className="border-0">
                                <Row>
                                    <Col xs="6">
                                        <h3 className="mt-2">나눔플로깅 등록</h3>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>봉사센터 로그인 아이디</Label>
                                            <Input
                                                value={form.centerLoginId}
                                                onChange={(e) => handleChange("centerLoginId", e.target.value)}
                                                placeholder="예: ph13651"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>1인 기준 월 기부 전달 금액</Label>
                                            <Input
                                                value={form.price}
                                                onChange={(e) => handleChange("price", e.target.value.replace(/[^0-9]/g, ""))}
                                                placeholder="숫자만 입력"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>우선순위</Label>
                                            <Input
                                                value={form.priority}
                                                onChange={(e) => handleChange("priority", e.target.value.replace(/[^0-9]/g, ""))}
                                                placeholder="숫자가 작을수록 먼저 노출"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>활성 여부</Label>
                                            <div>
                                                <label className="custom-toggle custom-toggle-success m-0">
                                                    <input
                                                        checked={!!form.active}
                                                        type="checkbox"
                                                        onChange={() => handleChange("active", !form.active)}
                                                    />
                                                    <span
                                                        className="custom-toggle-slider rounded-circle"
                                                        data-label-off="Off"
                                                        data-label-on="On"
                                                    />
                                                </label>
                                            </div>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>썸네일</Label>
                                            <Input type="file" onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}/>
                                            {thumbnailPreview && (
                                                <img
                                                    alt="thumbnail-preview"
                                                    src={thumbnailPreview}
                                                    style={{width: 160, height: 160, objectFit: "cover", marginTop: 12}}
                                                />
                                            )}
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>상세 이미지</Label>
                                            <Input type="file" onChange={(e) => setImageFile(e.target.files?.[0] || null)}/>
                                            {imagePreview && (
                                                <img
                                                    alt="image-preview"
                                                    src={imagePreview}
                                                    style={{width: 160, height: 160, objectFit: "cover", marginTop: 12}}
                                                />
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="text-right">
                                        <Button
                                            className="mr-2"
                                            color="secondary"
                                            onClick={() => history.push("/admin/sharingPlogging")}
                                        >
                                            목록
                                        </Button>
                                        <Button color="default" disabled={saving} onClick={handleSubmit}>
                                            {saving ? "저장중" : "등록"}
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </div>
                </Row>
            </Container>
        </>
    );
}

export default SharedPloggingForm;
